import * as React from "react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { FieldOption } from "@/config/forms/general";

interface HandleValue<T> {
  defaultVal: T;
  getSelect: (value: T) => string;
  getInput: (value: T) => string;
  onValueChange: (val: T | undefined) => void;
  constructValue: (v: {
    select: string | undefined;
    input: string;
  }) => T | undefined;
}

type EmptyType = Record<PropertyKey, never>;

export interface BaseInputSelectProps
  extends React.InputHTMLAttributes<HTMLDivElement> {
  options: FieldOption[];
  select?: Omit<React.ComponentProps<typeof Select>, "onValueChange">;
  input?: Omit<React.ComponentProps<typeof Input>, "defaultValue">;
  selectPlaceholder?: string;
}

export type InputSelectProps<T> = BaseInputSelectProps &
  (EmptyType | HandleValue<T>);

const InputSelect = <T,>({
  select,
  input,
  options,
  selectPlaceholder,
  defaultVal,
  className,
  constructValue,
  getInput,
  getSelect,
  onValueChange,
  ...props
}: InputSelectProps<T>) => {
  const liveSelectValue = React.useMemo(
    () => (defaultVal ? getSelect(defaultVal) : (select?.defaultValue ?? "")),
    [defaultVal, getSelect, select?.defaultValue],
  );
  const liveInputValue = React.useMemo(
    () => (defaultVal ? getInput(defaultVal) : ""),
    [defaultVal, getInput],
  );

  function doOnValueChange(select: string, input: string) {
    if (onValueChange) {
      onValueChange(constructValue({ select, input }));
    }
  }

  const [innerState, setInnerState] = React.useState({
    input: liveInputValue,
    select: liveSelectValue,
  });

  return (
    <div
      className={cn("flex w-full gap-2 border shadow-sm rounded-md bg-white", className)}
      {...props}
    >
      <Input
        {...input}
        value={innerState.input}
        className={cn("border-none shadow-none")}
        onChange={(v) => {
          setInnerState((s) => ({ ...s, input: v.target.value }));
          doOnValueChange(innerState.select, v.target.value);
        }}
      />
      <Select
        {...select}
        value={innerState.select}
        onValueChange={(v) => {
          setInnerState((s) => ({ ...s, select: v }));
          if (v !== "") doOnValueChange(v, innerState.input);
        }}
      >
        <SelectTrigger className={cn("w-fit border-none shadow-none bg-white")}>
          <SelectValue placeholder={selectPlaceholder} />
        </SelectTrigger>
        <SelectContent>
          {options?.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default InputSelect;
